import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import {
  Inbox, Clock, FileCheck, AlertTriangle, Loader2,
  CheckCircle2, Circle, Send, Stethoscope, Store, X, ChevronRight,
  Archive, MessageCircle, Sparkles,
} from "lucide-react";
import axios from "axios";
import {
  apiGet, apiPost, handleApiError, KpiCard, Btn, StatoBadge,
  formatDateTime, OspiteAvatar, API_URL, authHeaders,
} from "./shared";
import DoctorSelect from "../../../components/doctors/DoctorSelect";

const PRATICHE_PAGE_SIZE = 12;

const FILTRI_STATO = [
  { id: "tutte", label: "Tutti gli ospiti" },
  { id: "bloccata", label: "Bloccata" },
  { id: "attesa_medico", label: "Attesa medico" },
  { id: "pronta_invio", label: "Pronta invio" },
  { id: "completata", label: "Completata" },
  { id: "nessuna_pratica", label: "Nessuna pratica" },
];

const STATO_PRATICA_LABELS_EXTENDED = {
  attesa_medico: { label: "Attesa Medico", color: "bg-amber-50 text-amber-700 border border-amber-100" },
  pronta_invio: { label: "Pronta Invio", color: "bg-cyan-50 text-cyan-700 border border-cyan-100" },
  completata: { label: "Completata", color: "bg-emerald-50 text-emerald-700 border border-emerald-100" },
  bloccata: { label: "Bloccata", color: "bg-red-50 text-red-700 border border-red-100" },
  archiviata: { label: "Archiviata", color: "bg-slate-50 text-slate-500 border border-slate-200" },
  nessuna_pratica: { label: "Nessuna pratica", color: "bg-slate-50 text-slate-500 border border-slate-200" },
};

const CANALE_INVIO_LABELS = {
  email: "Email",
  whatsapp: "WhatsApp",
  manuale: "Manuale",
};

function AvanzamentoStep({ done, label, warning, onToggle, toggleTitle }) {
  const icon = done ? (
    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
  ) : warning ? (
    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
  ) : (
    <Circle className="w-3.5 h-3.5 text-slate-300 shrink-0" />
  );

  const content = (
    <>
      {icon}
      <span className={done ? "text-slate-700" : warning ? "text-amber-700" : "text-slate-400"}>{label}</span>
    </>
  );

  if (onToggle && !done) {
    return (
      <button
        type="button"
        onClick={onToggle}
        title={toggleTitle || "Segna come fatto"}
        className="flex items-center gap-1.5 text-xs text-left rounded-lg hover:bg-violet-50 px-1 -mx-1 py-0.5 transition"
      >
        {content}
      </button>
    );
  }

  return <div className="flex items-center gap-1.5 text-xs">{content}</div>;
}

function ComposeModal({ ospite, canale: initialCanale, token, onClose, onSent }) {
  const ospiteLabel = `${ospite?.ospite_cognome || ""} ${ospite?.ospite_nome || ""}`.trim();
  const isMedico = (initialCanale || "farmacia") === "medico";
  const [canale, setCanale] = useState(initialCanale || "farmacia");
  const [form, setForm] = useState({
    oggetto: isMedico
      ? `Sollecito ricette – ${ospiteLabel}`.trim()
      : `Richiesta ricette – ${ospiteLabel}`.trim(),
    messaggio: "",
    destinatario_email: isMedico ? (ospite?.medico_email || "") : "",
    doctor_id: isMedico ? (ospite?.doctor_id || "") : "",
    medico_nome: isMedico ? (ospite?.medico || "") : "",
  });
  const [sending, setSending] = useState(false);

  // Se manca l'email ma c'è doctor_id, recuperala dall'anagrafica medici
  useEffect(() => {
    if (canale !== "medico" || !token || !form.doctor_id || form.destinatario_email) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/doctors/${form.doctor_id}`, {
          headers: authHeaders(token),
        });
        if (cancelled) return;
        const doctor = res.data || {};
        setForm((prev) => ({
          ...prev,
          medico_nome: prev.medico_nome || doctor.name || "",
          destinatario_email: doctor.blistering_email || doctor.email || prev.destinatario_email,
        }));
      } catch {
        // Email compilabile a mano
      }
    })();
    return () => { cancelled = true; };
  }, [canale, token, form.doctor_id, form.destinatario_email]);

  const switchCanale = (next) => {
    setCanale(next);
    if (next === "medico") {
      setForm((prev) => ({
        ...prev,
        doctor_id: ospite?.doctor_id || prev.doctor_id || "",
        medico_nome: ospite?.medico || prev.medico_nome || "",
        destinatario_email: ospite?.medico_email || prev.destinatario_email || "",
        oggetto: prev.oggetto.startsWith("Richiesta ricette")
          ? `Sollecito ricette – ${ospiteLabel}`.trim()
          : prev.oggetto,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        doctor_id: "",
        medico_nome: "",
        destinatario_email: "",
      }));
    }
  };

  const handleSend = async () => {
    if (!form.messaggio.trim()) {
      toast.error("Scrivi un messaggio prima di inviare");
      return;
    }
    if (canale === "medico" && !form.doctor_id && !form.destinatario_email) {
      toast.error("Seleziona il medico di riferimento o inserisci un'email");
      return;
    }
    setSending(true);
    try {
      await apiPost("/comunicazioni", token, {
        canale,
        oggetto: form.oggetto,
        messaggio: form.messaggio,
        destinatario_email: form.destinatario_email,
        doctor_id: form.doctor_id || undefined,
        fascicolo_id: ospite?.fascicolo_id || undefined,
        pratica_id: ospite?.id && !String(ospite.id).startsWith("nopratica_") ? ospite.id : undefined,
      });
      toast.success("Messaggio inviato");
      onSent?.();
      onClose();
    } catch (err) {
      handleApiError(err, "Errore invio messaggio");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-900">
              {canale === "medico" ? "Sollecita medico" : "Nuovo messaggio"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {ospite?.ospite_cognome} {ospite?.ospite_nome}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => switchCanale("farmacia")}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-medium transition ${canale === "farmacia" ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              <Store className="w-4 h-4" />
              Farmacia
            </button>
            <button
              type="button"
              onClick={() => switchCanale("medico")}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-medium transition ${canale === "medico" ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              <Stethoscope className="w-4 h-4" />
              Medico
            </button>
          </div>

          {canale === "medico" && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-600 block">Medico di riferimento</label>
              <DoctorSelect
                token={token}
                value={form.doctor_id}
                displayName={form.medico_nome}
                onSelect={(doctor) => setForm({
                  ...form,
                  doctor_id: doctor.id,
                  medico_nome: doctor.name || "",
                  destinatario_email: doctor.blistering_email || doctor.email || "",
                })}
                onClear={() => setForm({ ...form, doctor_id: "", medico_nome: "", destinatario_email: "" })}
              />
              {form.medico_nome && (
                <p className="text-xs text-slate-500">
                  {form.medico_nome}
                  {form.destinatario_email ? ` · ${form.destinatario_email}` : " · email non configurata"}
                </p>
              )}
              {!form.doctor_id && !form.medico_nome && (
                <p className="text-xs text-amber-600">
                  Nessun medico di riferimento sul fascicolo: selezionalo dall&apos;elenco.
                </p>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Email destinatario</label>
                <input
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                  placeholder="medico@esempio.it"
                  value={form.destinatario_email}
                  onChange={(e) => setForm({ ...form, destinatario_email: e.target.value })}
                />
              </div>
            </div>
          )}

          {canale === "farmacia" && (
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Email farmacia</label>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                placeholder="farmacia@esempio.it"
                value={form.destinatario_email}
                onChange={(e) => setForm({ ...form, destinatario_email: e.target.value })}
              />
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Oggetto</label>
            <input
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              value={form.oggetto}
              onChange={(e) => setForm({ ...form, oggetto: e.target.value })}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Messaggio</label>
            <textarea
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              rows={5}
              placeholder={canale === "medico"
                ? "Es: La informiamo che attendiamo le ricette per il/la paziente..."
                : "Es: Si richiede la disponibilità del blister per il/la paziente..."}
              value={form.messaggio}
              onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
            />
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Btn variant="secondary" onClick={onClose}>Annulla</Btn>
            <Btn onClick={handleSend} disabled={sending || !form.messaggio.trim()}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Invia
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function SegnaInvioMedicoModal({ ospite, token, onClose, onDone }) {
  const [canale, setCanale] = useState("whatsapp");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiPost(`/pratiche/${ospite.id}/segna-invio-medico`, token, { canale, nota: nota.trim() || undefined });
      toast.success(canale === "whatsapp" ? "Segnato invio via WhatsApp" : canale === "sms" ? "Segnato invio via SMS" : "Segnato invio manuale al medico");
      onDone?.();
      onClose();
    } catch (err) {
      handleApiError(err, "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-900">Invio richieste al medico</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {ospite?.ospite_cognome} {ospite?.ospite_nome}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Segna l&apos;invio come già effettuato fuori da Affidya (es. WhatsApp al medico).
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCanale("whatsapp")}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-medium transition ${canale === "whatsapp" ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </button>
            <button
              type="button"
              onClick={() => setCanale("sms")}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-medium transition ${canale === "sms" ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              <Send className="w-4 h-4" />
              SMS
            </button>
            <button
              type="button"
              onClick={() => setCanale("manuale")}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-medium transition ${canale === "manuale" ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              <Send className="w-4 h-4" />
              Altro / Manuale
            </button>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Nota (opzionale)</label>
            <input
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              placeholder="Es: inviato su WhatsApp o SMS al Dr. Rossi"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Btn variant="secondary" onClick={onClose}>Annulla</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Conferma
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function FarmacoRow({ nome, status, matchInfo, side }) {
  const styles = {
    coperto: "bg-emerald-50 text-emerald-800 border-emerald-100",
    mancante: "bg-amber-50 text-amber-800 border-amber-100",
    extra: "bg-slate-50 text-slate-600 border-slate-200",
  };
  const labelsLeft = {
    coperto: "Coperto",
    mancante: "Manca",
  };
  const labelsRight = {
    coperto: "Abbinato",
    extra: "Extra",
  };
  const labels = side === "ricetta" ? labelsRight : labelsLeft;
  const isEquivalente = matchInfo && matchInfo.tipo_match && matchInfo.tipo_match !== "nome";
  const matchLabel = side === "ricetta"
    ? matchInfo?.farmaco
    : matchInfo?.corrispondenza;
  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${styles[status]}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium break-words min-w-0">{nome}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide shrink-0 mt-0.5">{labels[status]}</span>
      </div>
      {matchLabel && (
        <div className={`mt-1 text-[11px] ${isEquivalente ? "text-emerald-700" : "text-slate-500"}`}>
          {isEquivalente ? "equivalente di " : "→ "}
          <span className="font-medium">{matchLabel}</span>
        </div>
      )}
    </div>
  );
}

function PraticaModal({ praticaId, token, onClose, onArchived, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [matchingEquiv, setMatchingEquiv] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [showSegnaInvio, setShowSegnaInvio] = useState(false);
  const [showComposeMedico, setShowComposeMedico] = useState(false);

  const load = useCallback(async (opts = {}) => {
    if (!token || !praticaId) return;
    const silent = !!opts.silent;
    if (!silent) setLoading(true);
    try {
      const res = await apiGet(`/pratiche/${praticaId}`, token);
      setData(res);
    } catch (err) {
      handleApiError(err, "Errore caricamento pratica");
      if (!silent) onClose();
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, praticaId, onClose]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await apiPost(`/pratiche/${praticaId}/archivia`, token, {});
      toast.success("Pratica archiviata");
      onArchived?.();
      onClose();
    } catch (err) {
      handleApiError(err, "Errore archiviazione");
    } finally {
      setArchiving(false);
    }
  };

  const handleRiconosciEquivalenti = async () => {
    setMatchingEquiv(true);
    try {
      const res = await apiPost(`/confronto/pratiche/${praticaId}/auto-match`, token, {});
      const n = res.associazioni || 0;
      toast.success(
        n > 0
          ? `Riconosciuti ${n} equivalente${n === 1 ? "" : "i"} / abbinament${n === 1 ? "o" : "i"}`
          : "Nessun nuovo abbinamento trovato",
      );
      await load({ silent: true });
      onUpdated?.();
    } catch (err) {
      handleApiError(err, "Errore riconoscimento equivalenti");
    } finally {
      setMatchingEquiv(false);
    }
  };

  const handleInviaFarmaciaECompleta = async () => {
    const ricette = data?.ricette || [];
    if (ricette.length === 0) {
      toast.error("Nessuna ricetta da inviare alla farmacia");
      return;
    }
    setActionLoading(true);
    try {
      const res = await apiPost(`/pratiche/${praticaId}/invia-farmacia-e-completa`, token, {});
      const inviate = res?.inviate || 0;
      toast.success(
        inviate > 0
          ? "Ricette inviate alla farmacia e pratica segnata come completa"
          : "Pratica segnata come completa",
      );
      onUpdated?.();
      onClose();
    } catch (err) {
      handleApiError(err, "Errore invio alla farmacia");
    } finally {
      setActionLoading(false);
    }
  };

  const p = data?.pratica;
  const confronto = data?.confronto || {};
  const mancanti = confronto.mancanti_in_ricette || [];
  const praticaChiusa = !!(p && (p.archiviata || p.stato === "archiviata" || p.stato === "completata"));
  const canInviaECompleta = !!(confronto.completo && (data?.ricette || []).length > 0 && !praticaChiusa);
  const canSollecitaMedico = mancanti.length > 0 && !praticaChiusa;
  const hasFarmaciDaConfrontare = (confronto.totale_richiesti || 0) > 0 && (confronto.totale_ricette || 0) > 0;
  const canRiconosciEquiv = hasFarmaciDaConfrontare && !praticaChiusa;
  const steps = p ? [
    p.terapia_presente,
    p.richiesta_farmacia_ricevuta,
    p.invio_richieste_medico,
    p.ricette_medico_ricevute,
    p.invio_farmacia_effettuato,
  ] : [];
  const stepsDone = steps.filter(Boolean).length;

  const dettaglioByRichiesta = Object.fromEntries(
    (confronto.coperti_dettaglio || []).map((d) => [d.farmaco, d]),
  );
  const richiesteCol = [
    ...(confronto.coperti || []).map((nome) => ({
      nome,
      status: "coperto",
      matchInfo: dettaglioByRichiesta[nome],
    })),
    ...(confronto.mancanti_in_ricette || []).map((nome) => ({
      nome,
      status: "mancante",
    })),
  ];
  const ricetteCol = [
    ...(confronto.coperti_dettaglio || []).map((d) => ({
      nome: d.corrispondenza,
      status: "coperto",
      matchInfo: d,
    })),
    ...(confronto.extra_in_ricette || []).map((nome) => ({
      nome,
      status: "extra",
    })),
  ];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-3xl bg-white rounded-2xl border border-slate-200 shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900">Resoconto pratica</h3>
            {p && (
              <p className="text-xs text-slate-500 mt-0.5 truncate">
                {p.ospite_cognome} {p.ospite_nome}
                {p.stato && (
                  <span className="ml-2 inline-block align-middle">
                    <StatoBadge stato={p.stato} map={STATO_PRATICA_LABELS_EXTENDED} />
                  </span>
                )}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : !p ? (
            <p className="text-sm text-slate-500 text-center py-8">Pratica non trovata</p>
          ) : (
            <>
              {/* Avanzamento */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Avanzamento</span>
                  <span className="text-xs text-slate-400">{stepsDone}/5</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full rounded-full ${stepsDone === 5 ? "bg-emerald-500" : "bg-violet-500"}`}
                    style={{ width: `${(stepsDone / 5) * 100}%` }}
                  />
                </div>
                {(p.origine_portale_farmacia || (p.solleciti_farmacia_count || 0) > 0) && (
                  <div className="mb-3 flex flex-wrap gap-2 text-xs">
                    {p.origine_portale_farmacia && (
                      <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-medium text-emerald-800">
                        Richiesta dal Portale Farmacia
                      </span>
                    )}
                    {(p.solleciti_farmacia_count || 0) > 0 && (
                      <span className="rounded-full bg-amber-100 px-2.5 py-1 font-medium text-amber-800">
                        {p.solleciti_farmacia_count} solleciti dalla farmacia
                      </span>
                    )}
                  </div>
                )}
                {data?.avviso_fuori_ciclo && (
                  <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                    {data.avviso_fuori_ciclo}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <AvanzamentoStep done={p.terapia_presente} label="Terapia presente" />
                  <AvanzamentoStep done={p.richiesta_farmacia_ricevuta} label="Richiesta farmacia" />
                  <AvanzamentoStep
                    done={p.invio_richieste_medico}
                    label={
                      p.invio_richieste_medico
                        ? `Invio al medico (${CANALE_INVIO_LABELS[p.invio_richieste_medico_canale] || "sì"})`
                        : "Invio richieste al medico"
                    }
                    warning={p.richiesta_farmacia_ricevuta && !p.invio_richieste_medico}
                    onToggle={p.richiesta_farmacia_ricevuta && !p.invio_richieste_medico ? () => setShowSegnaInvio(true) : undefined}
                    toggleTitle="Segna invio manuale / WhatsApp"
                  />
                  <AvanzamentoStep done={p.ricette_medico_ricevute} label="Ricette medico" warning={!p.ricette_medico_ricevute} />
                  <AvanzamentoStep done={p.invio_farmacia_effettuato} label="Invio farmacia" />
                </div>
                {p.invio_richieste_medico_at && (
                  <p className="text-[11px] text-slate-400 mt-2">
                    Invio al medico: {formatDateTime(p.invio_richieste_medico_at)}
                    {p.invio_richieste_medico_nota ? ` — ${p.invio_richieste_medico_nota}` : ""}
                  </p>
                )}
              </div>

              {(p.anomalie || []).length > 0 && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                  {(p.anomalie || []).join("; ")}
                </div>
              )}

              {/* Confronto farmaci — due colonne */}
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">Farmaci richiesti vs ricette</h4>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {confronto.totale_richiesti || 0} richiesti · {confronto.totale_ricette || 0} in ricetta
                      {(confronto.coperti || []).length > 0
                        ? ` · ${(confronto.coperti || []).length} abbinati`
                        : ""}
                    </p>
                  </div>
                  {canRiconosciEquiv && (
                    <Btn
                      variant="soft"
                      className="text-xs gap-1.5"
                      onClick={handleRiconosciEquivalenti}
                      disabled={matchingEquiv || actionLoading}
                      title="Risolve i principi attivi e abbina equivalenti/generici (stessa molecola)"
                    >
                      {matchingEquiv
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5" />}
                      Riconosci equivalenti
                    </Btn>
                  )}
                </div>
                {(confronto.totale_richiesti || 0) === 0 && (confronto.totale_ricette || 0) === 0 ? (
                  <p className="text-sm text-slate-500 bg-slate-50 rounded-xl px-4 py-6 text-center border border-slate-100">
                    Nessun farmaco in richiesta o in ricetta per questa pratica.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
                      <div className="px-3 py-2 border-b border-slate-200 bg-white flex items-center gap-2">
                        <Store className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span className="text-xs font-semibold text-slate-800">Richieste farmacia</span>
                        <span className="ml-auto text-[11px] text-slate-400">{richiesteCol.length}</span>
                      </div>
                      <div className="p-2 space-y-1.5 max-h-64 overflow-y-auto">
                        {richiesteCol.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-4">Nessuna richiesta</p>
                        ) : (
                          richiesteCol.map((row) => (
                            <FarmacoRow
                              key={`req-${row.nome}`}
                              nome={row.nome}
                              status={row.status}
                              matchInfo={row.matchInfo}
                              side="richiesta"
                            />
                          ))
                        )}
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
                      <div className="px-3 py-2 border-b border-slate-200 bg-white flex items-center gap-2">
                        <Stethoscope className="w-3.5 h-3.5 text-violet-600 shrink-0" />
                        <span className="text-xs font-semibold text-slate-800">Ricette ricevute</span>
                        <span className="ml-auto text-[11px] text-slate-400">{ricetteCol.length}</span>
                      </div>
                      <div className="p-2 space-y-1.5 max-h-64 overflow-y-auto">
                        {ricetteCol.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-4">Nessuna ricetta</p>
                        ) : (
                          ricetteCol.map((row) => (
                            <FarmacoRow
                              key={`ric-${row.nome}`}
                              nome={row.nome}
                              status={row.status}
                              matchInfo={row.matchInfo}
                              side="ricetta"
                            />
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {confronto.completo && (
                  <p className="text-xs text-emerald-600 mt-2 font-medium">Tutti i farmaci richiesti risultano in ricetta.</p>
                )}
              </div>

              {/* Riepilogo documenti */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5">
                  <div className="text-slate-400 font-medium">Documenti richieste farmacia</div>
                  <div className="text-lg font-bold text-slate-900 mt-0.5">{(data.richieste || []).length}</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5">
                  <div className="text-slate-400 font-medium">Documenti ricette medico</div>
                  <div className="text-lg font-bold text-slate-900 mt-0.5">{(data.ricette || []).length}</div>
                </div>
              </div>
            </>
          )}
        </div>

        {!loading && p && !(p.archiviata || p.stato === "archiviata") && (
          <div className="px-5 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 shrink-0">
            {!confirmArchive ? (
              <>
                <Btn variant="danger" onClick={() => setConfirmArchive(true)} disabled={actionLoading}>
                  <Archive className="w-4 h-4" />
                  Archivia pratica
                </Btn>
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  {canSollecitaMedico && (
                    <Btn
                      variant="soft"
                      onClick={() => setShowComposeMedico(true)}
                      disabled={actionLoading}
                      title={`Mancano in ricetta: ${mancanti.join(", ")}`}
                    >
                      <Stethoscope className="w-4 h-4" />
                      Sollecita medico
                    </Btn>
                  )}
                  {canInviaECompleta && (
                    <Btn
                      variant="success"
                      onClick={handleInviaFarmaciaECompleta}
                      disabled={actionLoading}
                    >
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Invia alla farmacia e segna come completa
                    </Btn>
                  )}
                  <Btn variant="secondary" onClick={onClose} disabled={actionLoading}>Chiudi</Btn>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-slate-600 flex-1 min-w-[12rem]">
                  Archiviare questa pratica? Sparirà dalla panoramica attiva; potrai aprirne una nuova per l&apos;ospite.
                </p>
                <div className="flex gap-2">
                  <Btn variant="secondary" onClick={() => setConfirmArchive(false)} disabled={archiving}>Annulla</Btn>
                  <Btn variant="danger" onClick={handleArchive} disabled={archiving}>
                    {archiving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                    Conferma archivia
                  </Btn>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showSegnaInvio && p && (
        <SegnaInvioMedicoModal
          ospite={p}
          token={token}
          onClose={() => setShowSegnaInvio(false)}
          onDone={() => { load(); onUpdated?.(); }}
        />
      )}

      {showComposeMedico && p && (
        <ComposeModal
          ospite={p}
          canale="medico"
          token={token}
          onClose={() => setShowComposeMedico(false)}
          onSent={() => { load(); onUpdated?.(); }}
        />
      )}
    </div>
  );
}

function OspiteCard({ p, onOpenPratica, onCompose, onSegnaInvio }) {
  const stepsCompleted = [
    p.terapia_presente,
    p.richiesta_farmacia_ricevuta,
    p.invio_richieste_medico,
    p.ricette_medico_ricevute,
    p.invio_farmacia_effettuato,
  ].filter(Boolean).length;

  const hasAnomalie = (p.anomalie || []).length > 0;
  const invioLabel = p.invio_richieste_medico
    ? `Invio al medico (${CANALE_INVIO_LABELS[p.invio_richieste_medico_canale] || "sì"})`
    : "Invio richieste al medico";

  return (
    <div className={`rounded-2xl border bg-white p-4 flex flex-col gap-3 shadow-sm transition hover:shadow-md ${hasAnomalie ? "border-red-200" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <OspiteAvatar cognome={p.ospite_cognome} nome={p.ospite_nome} />
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate text-sm">
              {p.ospite_cognome} {p.ospite_nome}
            </div>
            {p.medico ? (
              <div className="text-[11px] text-slate-500 mt-0.5 truncate" title={p.medico_email || undefined}>
                Medico: {p.medico}
              </div>
            ) : null}
            <div className="text-[11px] text-slate-400 mt-0.5">
              Aggiornata: {formatDateTime(p.updated_at)}
            </div>
          </div>
        </div>
        <StatoBadge stato={p.stato} map={STATO_PRATICA_LABELS_EXTENDED} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] text-slate-500 font-medium">Avanzamento pratica</span>
          <span className="text-[11px] text-slate-400">{stepsCompleted}/5</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${stepsCompleted === 5 ? "bg-emerald-500" : stepsCompleted >= 2 ? "bg-violet-500" : "bg-amber-400"}`}
            style={{ width: `${(stepsCompleted / 5) * 100}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        <AvanzamentoStep done={p.terapia_presente} label="Terapia presente" />
        <AvanzamentoStep done={p.richiesta_farmacia_ricevuta} label="Richiesta farmacia" />
        <AvanzamentoStep
          done={p.invio_richieste_medico}
          label={invioLabel}
          warning={p.richiesta_farmacia_ricevuta && !p.invio_richieste_medico}
          onToggle={
            p.richiesta_farmacia_ricevuta && !p.invio_richieste_medico && p.stato !== "nessuna_pratica"
              ? () => onSegnaInvio?.(p)
              : undefined
          }
          toggleTitle="Segna invio manuale / WhatsApp"
        />
        <AvanzamentoStep done={p.ricette_medico_ricevute} label="Ricette medico" warning={!p.ricette_medico_ricevute} />
        <AvanzamentoStep done={p.invio_farmacia_effettuato} label="Invio farmacia" />
      </div>

      {hasAnomalie && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
          {(p.anomalie || []).join("; ")}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
        <Btn
          variant="secondary"
          className="flex-1 text-xs py-2 gap-1"
          onClick={() => onCompose(p, "medico")}
        >
          <Stethoscope className="w-3.5 h-3.5" />
          Sollecita medico
        </Btn>
        <Btn
          variant="secondary"
          className="flex-1 text-xs py-2 gap-1"
          onClick={() => onCompose(p, "farmacia")}
        >
          <Store className="w-3.5 h-3.5" />
          Scrivi farmacia
        </Btn>
        {p.stato !== "nessuna_pratica" && (
          <button
            type="button"
            onClick={() => onOpenPratica?.(p)}
            className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-semibold shrink-0 px-1"
          >
            Pratica
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

const AMBITO_LABELS = {
  tutti: "Tutti gli invii automatici recenti sono falliti",
  alcuni: "Solo alcuni invii automatici sono falliti",
};

function InvioAutomaticoProblemi({ token, reloadKey, onRetried }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setData(await apiGet("/invii-automatici/problemi", token));
    } catch {
      setData(null);
    }
  }, [token]);

  useEffect(() => { load(); }, [load, reloadKey]);

  if (!data || !data.totale) return null;

  const retry = async (p) => {
    setRetrying(p.id);
    try {
      await apiPost("/invii-automatici/riprova", token, { tipo: p.tipo, id: p.id });
      toast.success("Invio effettuato");
      onRetried?.();
    } catch (err) {
      handleApiError(err, "Invio non riuscito");
    } finally {
      setRetrying(null);
      load();
    }
  };

  return (
    <div className="rounded-2xl border border-red-200 bg-red-50/60 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">
              Problemi negli invii automatici ({data.totale})
            </p>
            {(data.riepilogo || []).filter((r) => r.falliti > 0).map((r) => (
              <p key={r.canale} className="text-xs text-red-700 mt-0.5">
                {r.canale === "medico" ? "Al medico" : "Alla farmacia"}: {r.falliti} da risolvere
                {" · "}{AMBITO_LABELS[r.ambito] || ""}
                {` (${r.riusciti_7gg} riusciti negli ultimi 7 giorni)`}
                {Object.keys(r.per_codice_label || {}).length > 0 && (
                  <> — {Object.entries(r.per_codice_label).map(([k, v]) => `${k}: ${v}`).join(", ")}</>
                )}
              </p>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-xs font-semibold text-red-700 hover:underline shrink-0"
        >
          {open ? "Nascondi" : "Dettagli"}
        </button>
      </div>

      {(data.struttura || []).map((s) => (
        <div key={s.codice} className="text-xs text-red-800 bg-white border border-red-100 rounded-xl px-3 py-2">
          {s.messaggio}
        </div>
      ))}

      {open && (
        <div className="space-y-2">
          {(data.problemi || []).map((p) => (
            <div key={`${p.tipo}-${p.id}`} className="bg-white border border-red-100 rounded-xl px-3 py-2 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900">
                  {p.ospite || "Ospite"}
                  <span className="ml-2 text-[11px] font-normal text-slate-500">
                    {p.canale === "medico" ? "richiesta → medico" : "ricetta → farmacia"}
                    {p.medico ? ` · ${p.medico}` : ""}
                  </span>
                </div>
                <div className="text-xs text-red-700">
                  <span className="font-semibold">{p.codice_label}</span>
                  {p.messaggio ? ` — ${p.messaggio}` : ""}
                </div>
                <div className="text-[11px] text-slate-400">
                  Ultimo tentativo {formatDateTime(p.ultimo_at)} · {p.tentativi} tentativ{p.tentativi === 1 ? "o" : "i"}
                  {p.ritento_automatico ? " · nuovo tentativo automatico ogni 30 min" : " · tentativi automatici esauriti"}
                </div>
              </div>
              <Btn
                variant="secondary"
                className="text-xs py-1.5 gap-1"
                disabled={retrying === p.id}
                onClick={() => retry(p)}
              >
                {retrying === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Riprova ora
              </Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Panoramica({ token }) {
  const { activeStruttura } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState("tutte");
  const [search, setSearch] = useState("");
  const [praticheLimit, setPraticheLimit] = useState(PRATICHE_PAGE_SIZE);
  const [composeTarget, setComposeTarget] = useState(null);
  const [praticaTarget, setPraticaTarget] = useState(null);
  const [segnaInvioTarget, setSegnaInvioTarget] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await apiGet("/panoramica", token);
      setData(res);
    } catch (err) {
      handleApiError(err, "Errore caricamento panoramica");
    } finally {
      setLoading(false);
    }
  }, [token, activeStruttura?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setPraticheLimit(PRATICHE_PAGE_SIZE);
  }, [filtroStato, search, activeStruttura?.id]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  const kpi = data?.kpi || {};
  const pratiche = data?.tutti_ospiti || data?.pratiche || [];

  const praticheFiltrate = pratiche.filter((p) => {
    if (filtroStato !== "tutte" && p.stato !== filtroStato) return false;
    if (search) {
      const hay = `${p.ospite_cognome || ""} ${p.ospite_nome || ""}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const praticheVisibili = praticheFiltrate.slice(0, praticheLimit);
  const altrePratiche = praticheFiltrate.length - praticheLimit;

  return (
    <div className="space-y-5">
      <InvioAutomaticoProblemi token={token} reloadKey={activeStruttura?.id} onRetried={load} />
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard icon={Inbox} label="Richieste farmacia aperte" value={kpi.richieste_aperte} subtitle={`${kpi.richieste_in_scadenza || 0} in scadenza`} tone="violet" />
        <KpiCard icon={Clock} label="Ricette attese dal medico" value={kpi.ricette_in_attesa} subtitle={`${kpi.ricette_da_verificare || 0} da verificare`} tone="amber" />
        <KpiCard icon={FileCheck} label="Ricette ricevute da inviare" value={kpi.ricette_ricevute} subtitle={`${kpi.ricette_da_inviare_farmacia || 0} pronte per farmacia`} tone="blue" />
        <KpiCard icon={AlertTriangle} label="Pratiche con problemi" value={kpi.pratiche_incomplete} subtitle={`${kpi.pratiche_in_attesa_medico || 0} in attesa medico`} tone="red" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-slate-900">
            Ospiti con deblistering attivo
            <span className="ml-2 text-xs font-normal text-slate-400">({pratiche.length} totali)</span>
          </h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
              </svg>
              <input
                className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30 w-40"
                placeholder="Cerca ospite..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap gap-2">
          {FILTRI_STATO.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFiltroStato(f.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition border ${filtroStato === f.id
                ? "bg-violet-600 text-white border-violet-600"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {f.label}
              {f.id !== "tutte" && (
                <span className="ml-1.5 opacity-70">
                  ({pratiche.filter((p) => p.stato === f.id).length})
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-4">
          {praticheFiltrate.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">
              {search || filtroStato !== "tutte"
                ? "Nessun ospite corrisponde ai filtri selezionati."
                : "Nessuna pratica aperta. Tutti gli ospiti sono in regola!"}
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {praticheVisibili.map((p) => (
                  <OspiteCard
                    key={p.id}
                    p={p}
                    onOpenPratica={(pratica) => setPraticaTarget(pratica)}
                    onCompose={(pratica, canale) => setComposeTarget({ pratica, canale })}
                    onSegnaInvio={(pratica) => setSegnaInvioTarget(pratica)}
                  />
                ))}
              </div>
              {altrePratiche > 0 && (
                <button
                  type="button"
                  className="w-full text-center text-xs text-violet-600 hover:underline py-3 mt-2"
                  onClick={() => setPraticheLimit((prev) => Math.min(prev + PRATICHE_PAGE_SIZE, praticheFiltrate.length))}
                >
                  Mostra altri {altrePratiche} ospiti
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {composeTarget && (
        <ComposeModal
          ospite={composeTarget.pratica}
          canale={composeTarget.canale}
          token={token}
          onClose={() => setComposeTarget(null)}
          onSent={load}
        />
      )}

      {segnaInvioTarget && (
        <SegnaInvioMedicoModal
          ospite={segnaInvioTarget}
          token={token}
          onClose={() => setSegnaInvioTarget(null)}
          onDone={load}
        />
      )}

      {praticaTarget && (
        <PraticaModal
          praticaId={praticaTarget.id}
          token={token}
          onClose={() => setPraticaTarget(null)}
          onArchived={load}
          onUpdated={load}
        />
      )}
    </div>
  );
}
